import InventoryItemStatusTitle from './enums/InventoryItemStatusTitle';
import InventoryItemStatusColor from './enums/InventoryItemStatusColor';
import RequestStatusTitle from './enums/RequestStatusTitle';
import RequestStatusColor from './enums/RequestStatusColor';
import { SYSTEM_ATTRIBUTE, itemCityName, systemAttributeValue } from './inventoryAttributes';

/**
 * Связь оборудования с обращениями (7-G1): что показывать в строке с каждой стороны.
 *
 * Решения вынесены из компонентов, чтобы проверяться тестами без монтирования:
 * вкладка «Оборудование» в обращении и вкладка «Обращения» в карточке предмета.
 *
 * Доступ здесь не вычисляется. Список оборудования обращения бэкенд уже сузил до области
 * просмотра, список обращений — до тех, что пользователь может открыть.
 */

export const EMPTY_VALUE = '—';

/**
 * Длина описания в строке обращения. Полный текст — в самой заявке, сюда нужна
 * одна-две строки, чтобы узнать, о чём она.
 */
export const DESCRIPTION_LIMIT = 140;

/**
 * Строка предмета во вкладке «Оборудование» обращения.
 *
 * Идентификатор — серийный номер, а не выдуманный инвентарный: отдельного инвентарного
 * номера у предмета нет. № позиции показывается рядом, подписанным как № позиции.
 *
 * @param {Object} item Предмет в том виде, в каком его отдаёт список оборудования.
 * @returns {Object}
 */
export function linkedItemRow(item) {
    const serial = systemAttributeValue(item, SYSTEM_ATTRIBUTE.SERIAL_NUMBER);
    const city = itemCityName(item);

    return {
        id: item.id,
        name: item.name || EMPTY_VALUE,
        serial: serial || EMPTY_VALUE,
        city: city || EMPTY_VALUE,
        statusTitle: InventoryItemStatusTitle[item.status] || EMPTY_VALUE,
        statusColor: InventoryItemStatusColor[item.status] || null,
    };
}

/**
 * Идентификаторы уже привязанных предметов — чтобы поиск не предлагал их второй раз.
 *
 * @param {Object[]} items
 * @returns {Set<number>}
 */
export function linkedIds(items) {
    return new Set((Array.isArray(items) ? items : []).map((item) => item.id));
}

/**
 * Результаты поиска с пометкой «уже привязано».
 *
 * Не выкидываются, а помечаются: исчезнувшая из выдачи позиция, которую человек точно
 * ищет по серийнику, выглядит как «не найдено», а «уже привязано» объясняет, почему её
 * нельзя выбрать.
 *
 * @param {Object[]} results
 * @param {Object[]} linked
 * @returns {Object[]} Строки linkedItemRow() с полем isLinked.
 */
export function pickerRows(results, linked) {
    const ids = linkedIds(linked);

    return (Array.isArray(results) ? results : []).map((item) => ({
        ...linkedItemRow(item),
        isLinked: ids.has(item.id),
    }));
}

/**
 * Начало текста, обрезанное по границе слова.
 *
 * @param {string|null} text
 * @param {number} limit
 * @returns {string}
 */
export function shortText(text, limit = DESCRIPTION_LIMIT) {
    const normalized = String(text || '')
        .replace(/\s+/g, ' ')
        .trim();

    if (normalized.length <= limit) {
        return normalized;
    }

    const cut = normalized.slice(0, limit);
    const lastSpace = cut.lastIndexOf(' ');

    return `${(lastSpace > limit / 2 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

/**
 * Строка обращения во вкладке «Обращения» карточки оборудования.
 *
 * @param {Object} request Сжатая строка из GET /inventory/items/{id}/requests.
 * @returns {Object}
 */
export function requestLinkRow(request) {
    return {
        id: request.id,
        service: request.service_title || request.category_title || EMPTY_VALUE,
        description: shortText(request.description),
        statusTitle: RequestStatusTitle[request.status] || EMPTY_VALUE,
        statusColor: RequestStatusColor[request.status] || null,
        createdAt: request.created_at || null,
    };
}
